import {
    loadIdToken,
    setIdToken,
    removeIdToken,
    loadProfile,
} from 'Utils/jwtUtil';
import util from 'Utils/commonUtil';
import getErrorMsg from '../error';
import {NOTIFICATION} from 'Actions/notification';

import 'cross-fetch/polyfill';

export function injectAuthorizationHeader() {
    return {Token: loadIdToken()};
}

/**
 * A utility to call a restful service.
 *
 * @param url The restful service end point.
 * @param config The config object of the call. Can be null.
 * @param request The request action.
 * @param onRequestSuccess The callback function to create request success action.
 *                 The function expects response json payload as its argument.
 * @param onRequestFailure The callback function to create request failure action.
 *                 The function expects error as its argument.
 */
export function callApi(
    url,
    config,
    readyRequest,
    onRequestSuccess,
    onRequestFailure,
    callback,
) {
    return (dispatch) => {
        config = config || {};

        // Request headers
        let options = {url, method: config.method || 'POST'};

        // ready request.
        util.isFunction(readyRequest) && dispatch(readyRequest());

        if (config.data) options.data = config.data;

        return new Promise((resolve, reject) => {
            request(options, dispatch)
                .then((res) => {
                    util.isFunction(callback) && callback(res);

                    if (util.isArray(onRequestSuccess)) {
                        onRequestSuccess.forEach(
                            (func) =>
                                typeof func(res) !== 'undefined' &&
                                func(res) &&
                                dispatch(func(res)),
                        );
                    } else {
                        onRequestSuccess && dispatch(onRequestSuccess(res));
                    }

                    resolve(res);
                })
                .catch((error) => {
                    if (error.status === 'token_expired') {
                        removeIdToken();
                        window.location.replace(
                            `${window.location.origin}/adminLogin`,
                        );
                    } else {
                        onRequestFailure && dispatch(onRequestFailure(error));
                        reject(error);
                    }
                });
        });
    };
}

/** Literal get call to api without the interaction with store
 * @param url The restful service end point.
 */
export const get = (url, params) => async (dispatch) =>
    request({url, method: 'GET', params}, dispatch);

/** Literal put call to api without the interaction with store
 * @param url The restful service end point.
 */
export const put = (url, params) => async (dispatch) =>
    request({url, method: 'PUT', params}, dispatch);

/** Literal post call to api without the interaction with store
 * @param url The restful service end point.
 * @param data The data to be passed in body.
 */
export const post = (url, data) => (dispatch) =>
    request({url, method: 'POST', data}, dispatch);

export const uploadFile = (url, data) => (dispatch) =>
    request({url, method: 'POST', data, isForm: true}, dispatch);

/**
 * A utility to call a restful service.
 * @param options {Object}
 * @param options.url {String} request url
 * @param options.method {String} request method, default value is 'GET'.
 * @param options.params {Object, String} request query or search (?id=1)
 * @param options.data {Object} request body data or form data.
 * @param options.isForm {Boolean} form data Flag.
 * @param options.headers {Object} request headers.
 * @return {Promise}
 */
export const request = (options, dispatch) => {
    let url = options.url;
    let tempParams = '';
    let formData = new FormData();
    let headers = new Headers();
    const method = options.method ? options.method.toLocaleUpperCase() : 'GET';
    const params = options.params || {};
    const data = options.data;
    const requestType =
        url === '/api/upload/file'
            ? options.data && options.data.type
            : undefined;
    const isForm = options.isForm;
    const customHeaders = options.headers || {};
    let requestKey = method + url;

    if (!dispatch) throw new Error("'dispatch' argument is undefined.");

    // Get request params.
    if (/\?/.test(url) && !options.params) {
        let temp = url.split('?');
        let arr = temp[1].split('&');
        url = temp[0];
        tempParams = '?' + temp[1];
        requestKey += params;

        arr.forEach((item) => {
            let itemTemp = item.split('=');

            params[itemTemp[0]] = itemTemp[1];
        });
    } else if (util.isString(params)) {
        if (/^\?/.test(params)) {
            tempParams = params;
        } else {
            tempParams = '?' + params;
        }
        requestKey += params;
    } else if (options.params) {
        tempParams = '?';
        let arr = [];
        Object.keys(params).forEach((key) => arr.push(`${key}=${params[key]}`));
        tempParams += arr.join('&');
        requestKey += tempParams;
    }

    // Get quest data.
    if (data) {
        requestKey += JSON.stringify(data);
        isForm === true &&
            Object.keys(data).forEach((key) => formData.append(key, data[key]));
    }
    // Set request headers
    if (isForm !== true) headers.append('Content-Type', 'application/json');
    if (url.indexOf('/identity/wechat/auth/unbind') === -1) {
        headers.append('Token', loadIdToken());
        headers.append('Cache-Control', 'max-age=300');
    }
    if (url.indexOf('/api/download/pic') !== -1) {
        headers.append('Content-Type', 'application/json');
        headers.append('Accept', 'application/json');
    }

    Object.keys(customHeaders).forEach((key) => {
        if (headers.has(key)) {
            headers.set(key, customHeaders[key]);
        } else {
            headers.append(key, customHeaders[key]);
        }
    });

    return new Promise((resolve, reject) => {
        window.requestList = window.requestList || {};
        if (window.requestList[requestKey]) {
            return;
        } else {
            window.requestList[requestKey] = true;
        }

        let profile = loadProfile();
        fetch(url + tempParams, {
            method,
            headers,
            body:
                isForm === true
                    ? formData
                    : data
                    ? JSON.stringify(data)
                    : undefined,
            credentials:
                url.indexOf('/api/customer/login/token') !== -1 ||
                url.indexOf('/identity/wechat/auth/unbind') !== -1 ||
                url.indexOf('/api/sso/user/login') !== -1
                    ? 'include'
                    : 'omit',
        })
            .then((res) => {
                if (res.status === 413) {
                    throw {status: 413};
                }

                if (res.status === 404) {
                    console.error('API not found: ' + url + res.status);
                    throw {status: 404};
                }
                if (res.status === 502) {
                    throw {status: 502};
                }

                if (res.status === 401) {
                    removeIdToken();
                    if (profile && typeof profile.roleType !== 'undefined') {
                        if (profile.roleType === 0) {
                            window.location.replace(
                                `${window.location.origin}/adminLogin`,
                            );
                        } else {
                            window.location.replace(
                                `${window.location.origin}/adminLogin`,
                            );
                        }
                    } else {
                        if (url.indexOf('/api/sso/user/login') !== -1) {
                            return res.json();
                        } else {
                            window.location.replace(
                                `${window.location.origin}/adminLogin`,
                            );
                        }
                    }
                } else {
                    let token = res.headers.get('Token');
                    if (token) setIdToken(token);

                    return res.json();
                }
            })
            .then((data) => {
                // check reponse status is success
                delete window.requestList[requestKey];
                console.log(
                    '6666666666666666666666666data.status',
                    data.status,
                );
                switch (data.status) {
                    case 'unbind':
                    case 'success':
                    case '200':
                        resolve(data);
                        break;
                    case 'unauthorized':
                        if (url.indexOf('/api/sso/user/login') !== -1) {
                            resolve(data);
                            break;
                        } else {
                            window.location.replace(
                                `${window.location.origin}/adminLogin`,
                            );
                        }
                    default:
                        if (data.status === 'token_expired') {
                            // window.location.href = `${ window.location.origin }/${ process.env.http_env }/adminLogin`;
                            removeIdToken();
                            if (
                                profile &&
                                typeof profile.roleType !== 'undefined'
                            ) {
                                if (profile.roleType === 0) {
                                    window.location.replace(
                                        `${window.location.origin}/adminLogin`,
                                    );
                                } else {
                                    window.location.replace(
                                        `${window.location.origin}/adminLogin`,
                                    );
                                }
                            } else {
                                window.location.replace(
                                    `${window.location.origin}/adminLogin`,
                                );
                            }
                        } else {
                            let msg = getErrorMsg(
                                data.status,
                                url,
                                method,
                                params,
                                data,
                                requestType,
                            );

                            if (msg)
                                dispatch({
                                    type: NOTIFICATION,
                                    payload: {msg, type: 'danger'},
                                });
                            reject(data);
                        }
                }
            })
            .catch((err) => {
                // If request is fail.
                delete window.requestList[requestKey];

                if (err.status === 'token_expired') {
                    removeIdToken();
                    window.location.replace(
                        `${window.location.origin}/adminLogin`,
                    );
                } else if (err.status === 413) {
                    dispatch({
                        type: NOTIFICATION,
                        payload: {
                            msg: '大小超过限制',
                            type: 'danger',
                        },
                    });
                } else if (err.status === 404) {
                    dispatch({
                        type: NOTIFICATION,
                        payload: {
                            msg: '接口不存在或地址错误（404）',
                            type: 'danger',
                        },
                    });
                    // ✅ 新增：502 提示
                } else if (err.status === 502) {
                    dispatch({
                        type: NOTIFICATION,
                        payload: {
                            msg: '服务暂时不可用，请稍后重试（502）',
                            type: 'danger',
                        },
                    });
                } else {
                    dispatch({
                        type: NOTIFICATION,
                        payload: {
                            msg: '网络异常，请检查您的网络',
                            type: 'danger',
                        },
                    });
                }

                reject(err);
            });
    });
};

export function downloadFile(url, data, fileName) {
    try {
        const options = {
            method: 'POST',
            body: JSON.stringify(data),
            headers: new Headers({
                'Content-Type': 'application/json',
                Token: loadIdToken(),
            }),
            requestType: 'arraybuffer',
            responseType: 'blob',
        };
        return fetch(url, options)
            .then((res) => {
                return res.blob();
            })
            .then((blob) => {
                let imageUrl = window.URL.createObjectURL(new Blob([blob]));
                let imageObj = imageUrl;
                return imageObj;
            });
    } catch (err) {
        console.error(err);
    }
}

export function downloadProxyDeliverFile(url, data, fileName) {
    try {
        const sanitizeMimeType = (type) => {
            if (!type || typeof type !== 'string') {
                return '';
            }

            const semiIndex = type.indexOf(';');
            return semiIndex > -1 ? type.slice(0, semiIndex).trim() : type;
        };

        const isInvalidMimeType = (type) => {
            if (!type) return true;
            const lowered = type.toLowerCase();
            return (
                lowered === 'application/octet-stream' ||
                lowered === 'application/json' ||
                lowered === 'text/plain'
            );
        };

        const inferMimeType = (path) => {
            if (!path || typeof path !== 'string') {
                return '';
            }

            const cleanPath = path.split('?')[0];
            const ext = cleanPath.split('.').pop().toLowerCase();

            switch (ext) {
                case 'jpg':
                case 'jpeg':
                    return 'image/jpeg';
                case 'png':
                    return 'image/png';
                case 'gif':
                    return 'image/gif';
                case 'bmp':
                    return 'image/bmp';
                case 'webp':
                    return 'image/webp';
                case 'svg':
                    return 'image/svg+xml';
                default:
                    return '';
            }
        };

        const options = {
            method: 'POST',
            body: JSON.stringify(data),
            headers: new Headers({
                'Content-Type': 'application/json',
                Token: loadIdToken(),
            }),
            requestType: 'arraybuffer',
            responseType: 'blob',
        };

        return fetch(url, options).then((res) => {
            return res.blob().then((blob) => {
                const headerMime = sanitizeMimeType(
                    res.headers.get('content-type'),
                );
                const blobMime = sanitizeMimeType(blob.type);
                const inferredMime = inferMimeType(data && data.path);

                const normalizedMime =
                    [headerMime, blobMime, inferredMime, 'image/jpeg'].find(
                        (type) => type && !isInvalidMimeType(type),
                    ) || 'image/jpeg';

                const normalizedBlob =
                    !isInvalidMimeType(blobMime) && blobMime === normalizedMime
                        ? blob
                        : new Blob([blob], {type: normalizedMime});

                let imageUrl = window.URL.createObjectURL(normalizedBlob);
                return imageUrl;
            });
        });
    } catch (err) {
        console.error(err);
    }
}

export function downloadPickOrderFile(url, data, fileName) {
    try {
        const options = {
            method: 'POST',
            body: JSON.stringify(data),
            headers: new Headers({
                'Content-Type': 'application/json',
                Token: loadIdToken(),
            }),
            requestType: 'arraybuffer',
            responseType: 'blob',
        };
        return fetch(url, options)
            .then((res) => {
                return res.blob();
            })
            .then((blob) => {
                let url = window.URL.createObjectURL(
                    new Blob([blob], {type: 'application/pdf'}),
                );
                return url;
            });
    } catch (err) {
        console.error(err);
    }
}

export function downloadPdfFile(currentUrl, data, pdfType) {
    try {
        let url = '';
        if (pdfType === 'shipment') {
            url = currentUrl + `?shipmentIds=${data.shipmentIds}`;
        } else if (pdfType === 'recommend') {
            url = currentUrl + `?orderIds=${data.orderIds}`;
        } else if (pdfType === 'pickOrder') {
            url = currentUrl + `?ids=${data.ids}`;
        }
        return fetch(url, {
            method: 'get',
            responseType: 'arraybuffer',
            headers: new Headers({
                'Content-Type': 'application/json',
                Token: loadIdToken(),
            }),
        })
            .then((res) => {
                return res.blob();
            })
            .then((blob) => {
                let url = window.URL.createObjectURL(
                    new Blob([blob], {type: 'application/pdf'}),
                );
                // const link = document.createElement('a');
                // link.style.display = "none";
                // link.href = url;
                // link.setAttribute('download', "发货单.pdf");
                // document.body.appendChild(link);
                // link.click();
                // document.body.removeChild(link);
                return url;
            });
    } catch (err) {
        console.error(err);
    }
}

// Add Clock Commets Dialog Function
export function uploadAudio(url, contentType, blob) {
    try {
        const options = {
            method: 'POST',
            body: blob,
            headers: new Headers({
                // 'content-type': contentType,
                Token: loadIdToken(),
            }),
        };
        return fetch(url, options).then((res) => {
            return res;
        });
    } catch (err) {
        console.error(err);
    }
}
